import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileText, Copy, Check, Github } from 'lucide-react';

const Publications: React.FC = () => {
  const { t } = useTranslation();
  const [showGDDSCitation, setShowGDDSCitation] = useState(false);
  const [showFineTuningCitation, setShowFineTuningCitation] = useState(false);
  const [showLLMCitation, setShowLLMCitation] = useState(false);
  const [showZeroShotCitation, setShowZeroShotCitation] = useState(false);
  const [showSGDICLCitation, setShowSGDICLCitation] = useState(false);
  const [copiedGDDS, setCopiedGDDS] = useState(false);
  const [copiedFineTuning, setCopiedFineTuning] = useState(false);
  const [copiedLLM, setCopiedLLM] = useState(false);
  const [copiedZeroShot, setCopiedZeroShot] = useState(false);
  const [copiedSGDICL, setCopiedSGDICL] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      {/* Header Section */}
      <div className="text-center mb-12">
        <motion.h1 
          className="font-display text-5xl md:text-7xl font-semibold text-stone-800 mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t('publications.title')}
        </motion.h1>
        <motion.p 
          className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {t('publications.subtitle')}
        </motion.p>
      </div>

      {/* Conference Papers */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <h2 className="font-display text-3xl font-semibold text-stone-800 mb-6 flex items-center">
          <span className="w-8 h-px bg-stone-500 mr-4"></span>
          {t('publications.conferencePapers')}
        </h2>
        <div className="space-y-6">
          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* GIF Image */}
              <div className="flex-shrink-0">
                <div className="w-full md:w-64 h-48 md:h-64 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 bg-white flex items-center justify-center">
                  <img 
                    src="/files/publications/gifs_and_imgs/sepo.gif" 
                    alt="Fine-tuning discrete diffusion models with policy gradient methods visualization" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <a href="https://arxiv.org/abs/2502.01384" target="_blank" rel="noopener noreferrer" 
                   className="font-display text-3xl font-semibold text-[#4f4339] transition-colors duration-300 group-hover:text-[#6f6257]">
                  {t('publications.papers.fineTuning.title')}
                </a>
                <div className="flex items-center mt-2 mb-3">
                  <span className="text-sm font-semibold text-[#6f6257] bg-[#f3ede6] px-3 py-1 rounded-full">
                    NeurIPS 2025
                  </span>
                </div>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.fineTuning.authors')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('publications.papers.fineTuning.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://arxiv.org/abs/2502.01384" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('publications.paper')}
                  </a>
                  <a href="https://github.com/ozekri/SEPO" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-3 py-1 bg-[#ede9e2] text-stone-700 text-sm rounded-lg hover:bg-[#e4ddd3] transition-colors duration-200">
                    <Github className="w-4 h-4 mr-1" />
                    Code
                  </a>
                  <button 
                    onClick={() => setShowFineTuningCitation(!showFineTuningCitation)}
                    className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Cite
                  </button>
                </div>
                
                {showFineTuningCitation && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`@article{zekri2025fine,
  title={Fine-tuning discrete diffusion models with policy gradient methods},
  author={Zekri, Oussama and Boull{\\'e}, Nicolas},
  journal={arXiv preprint arXiv:2502.01384},
  year={2025}
}`);
                          setCopiedFineTuning(true);
                          setTimeout(() => setCopiedFineTuning(false), 2000);
                        }}
                        className="inline-flex items-center px-2 py-1 bg-[#f3ede6] text-[#6f6257] text-xs rounded hover:bg-[#ece3da] transition-colors duration-200"
                      >
                        {copiedFineTuning ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copiedFineTuning ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`@article{zekri2025fine,
  title={Fine-tuning discrete diffusion models with policy gradient methods},
  author={Zekri, Oussama and Boull{\\'e}, Nicolas},
  journal={arXiv preprint arXiv:2502.01384},
  year={2025}
}`}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.0 }}
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Paper Image */}
              <div className="flex-shrink-0">
                <div className="w-full md:w-64 h-48 md:h-64 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <img 
                    src="/files/publications/gifs_and_imgs/zero_shot.png" 
                    alt="Zero-shot Model-based Reinforcement Learning using Large Language Models visualization" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <a href="https://arxiv.org/abs/2410.11711" target="_blank" rel="noopener noreferrer" 
                   className="font-display text-3xl font-semibold text-[#4f4339] transition-colors duration-300 group-hover:text-[#6f6257]">
                  {t('publications.papers.zeroShot.title')}
                </a>
                <div className="flex items-center mt-2 mb-3">
                  <span className="text-sm font-semibold text-[#6f6257] bg-[#f3ede6] px-3 py-1 rounded-full">
                    {t('publications.papers.zeroShot.conference')}
                  </span>
                </div>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.zeroShot.authors')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('publications.papers.zeroShot.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://arxiv.org/abs/2410.11711" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('publications.paper')}
                  </a>
                  <a href="https://github.com/abenechehab/dicl" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-3 py-1 bg-[#ede9e2] text-stone-700 text-sm rounded-lg hover:bg-[#e4ddd3] transition-colors duration-200">
                    <Github className="w-4 h-4 mr-1" />
                    Code
                  </a>
                  <button 
                    onClick={() => setShowZeroShotCitation(!showZeroShotCitation)}
                    className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Cite
                  </button>
                </div>
                
                {showZeroShotCitation && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`@article{benechehab2024zero,
  title={Zero-shot model-based reinforcement learning using large language models},
  author={Benechehab, Abdelhakim and Hili, Youssef Attia El and Odonnat, Ambroise and Zekri, Oussama and Thomas, Albert and Paolo, Giuseppe and Filippone, Maurizio and Redko, Ievgen and K{\\'e}gl, Bal{\\'a}zs},
  journal={arXiv preprint arXiv:2410.11711},
  year={2024}
}`);
                          setCopiedZeroShot(true);
                          setTimeout(() => setCopiedZeroShot(false), 2000);
                        }}
                        className="inline-flex items-center px-2 py-1 bg-[#f3ede6] text-[#6f6257] text-xs rounded hover:bg-[#ece3da] transition-colors duration-200"
                      >
                        {copiedZeroShot ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copiedZeroShot ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`@article{benechehab2024zero,
  title={Zero-shot model-based reinforcement learning using large language models},
  author={Benechehab, Abdelhakim and Hili, Youssef Attia El and Odonnat, Ambroise and Zekri, Oussama and Thomas, Albert and Paolo, Giuseppe and Filippone, Maurizio and Redko, Ievgen and K{\\'e}gl, Bal{\\'a}zs},
  journal={arXiv preprint arXiv:2410.11711},
  year={2024}
}`}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Preprints */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.0 }}
      >
        <h2 className="font-display text-3xl font-semibold text-stone-800 mb-6 flex items-center">
          <span className="w-8 h-px bg-stone-500 mr-4"></span>
          {t('publications.preprints')}
        </h2>
        <div className="space-y-6">
          <motion.div
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.1 }}
          >
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="w-full md:w-64 h-48 md:h-64 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 bg-[#0f1218]">
                  <video
                    className="w-full h-full object-contain"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    preload="metadata"
                  >
                    <source src="/files/publications/gdds/videos/gdds_forward_noising_teaser.mp4" type="video/mp4" />
                    Your browser does not support the teaser video.
                  </video>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <a href="/gdds"
                   className="font-display text-3xl font-semibold text-[#4f4339] transition-colors duration-300 group-hover:text-[#6f6257]">
                  {t('publications.papers.gdds.title')}
                </a>
                <div className="flex items-center mt-2 mb-3">
                  <span className="text-sm font-semibold text-[#8a7767] bg-[#f5efe8] px-3 py-1 rounded-full">
                    {t('publications.preprint')}
                  </span>
                </div>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.gdds.authors')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('publications.papers.gdds.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://arxiv.org/abs/2603.21342" target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('publications.paper')}
                  </a>
                  <a href="/gdds"
                     className="inline-flex items-center px-3 py-1 bg-[#ede9e2] text-[#6f6257] text-sm rounded-lg hover:bg-[#e4ddd3] transition-colors duration-200">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('publications.projectPage')}
                  </a>
                  <a href="https://github.com/ozekri/gdds" target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center px-3 py-1 bg-[#ede9e2] text-stone-700 text-sm rounded-lg hover:bg-[#e4ddd3] transition-colors duration-200">
                    <Github className="w-4 h-4 mr-1" />
                    Code
                  </a>
                  <button
                    onClick={() => setShowGDDSCitation(!showGDDSCitation)}
                    className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Cite
                  </button>
                </div>

                {showGDDSCitation && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`@article{zekri2026generalized,
  title={Generalized Discrete Diffusion from Snapshots},
  author={Zekri, Oussama and Uscidda, Th{\\'e}o and Boull{\\'e}, Nicolas and Korba, Anna},
  journal={arXiv preprint arXiv:2603.21342},
  year={2026}
}`);
                          setCopiedGDDS(true);
                          setTimeout(() => setCopiedGDDS(false), 2000);
                        }}
                        className="inline-flex items-center px-2 py-1 bg-[#f3ede6] text-[#6f6257] text-xs rounded hover:bg-[#ece3da] transition-colors duration-200"
                      >
                        {copiedGDDS ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copiedGDDS ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`@article{zekri2026generalized,
  title={Generalized Discrete Diffusion from Snapshots},
  author={Zekri, Oussama and Uscidda, Th{\\'e}o and Boull{\\'e}, Nicolas and Korba, Anna},
  journal={arXiv preprint arXiv:2603.21342},
  year={2026}
}`}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.2 }}
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* GIF Image */}
              <div className="flex-shrink-0">
                <div className="w-full md:w-64 h-48 md:h-64 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <img 
                    src="/assets/llm_as_mc_toy.gif" 
                    alt="Large Language Models as Markov Chains visualization" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <a href="https://arxiv.org/abs/2410.02724" target="_blank" rel="noopener noreferrer" 
                   className="font-display text-3xl font-semibold text-[#4f4339] transition-colors duration-300 group-hover:text-[#6f6257]">
                  {t('publications.papers.llmMarkov.title')}
                </a>
                <div className="flex items-center mt-2 mb-3">
                  <span className="text-sm font-semibold text-[#8a7767] bg-[#f5efe8] px-3 py-1 rounded-full">
                    {t('publications.preprint')}
                  </span>
                </div>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.llmMarkov.authors')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('publications.papers.llmMarkov.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://arxiv.org/abs/2410.02724" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('publications.paper')}
                  </a>
                  <button 
                    onClick={() => setShowLLMCitation(!showLLMCitation)}
                    className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Cite
                  </button>
                </div>
                
                {showLLMCitation && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`@article{zekri2024large,
  title={Large language models as markov chains},
  author={Zekri, Oussama and Odonnat, Ambroise and Benechehab, Abdelhakim and Bleistein, Linus and Boull{\\'e}, Nicolas and Redko, Ievgen},
  journal={arXiv preprint arXiv:2410.02724},
  year={2024}
}`);
                          setCopiedLLM(true);
                          setTimeout(() => setCopiedLLM(false), 2000);
                        }}
                        className="inline-flex items-center px-2 py-1 bg-[#f3ede6] text-[#6f6257] text-xs rounded hover:bg-[#ece3da] transition-colors duration-200"
                      >
                        {copiedLLM ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copiedLLM ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
{`@article{zekri2024large,
  title={Large language models as markov chains},
  author={Zekri, Oussama and Odonnat, Ambroise and Benechehab, Abdelhakim and Bleistein, Linus and Boull{\\'e}, Nicolas and Redko, Ievgen},
  journal={arXiv preprint arXiv:2410.02724},
  year={2024}
}`}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Workshop Papers */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.6 }}
      >
        <h2 className="font-display text-3xl font-semibold text-stone-800 mb-6 flex items-center">
          <span className="w-8 h-px bg-stone-500 mr-4"></span>
          {t('publications.workshopPapers')}
        </h2>
        <div className="space-y-6">
          <motion.div 
            className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-stone-200/70 hover:shadow-xl transition-all duration-300 group"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.8 }}
          >
            <div className="flex flex-col md:flex-row gap-6">
              {/* Paper Image */}
              <div className="flex-shrink-0">
                <div className="w-full md:w-64 h-48 md:h-64 rounded-lg overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <img 
                    src="/files/publications/gifs_and_imgs/sgd_icl.png" 
                    alt="Can LLMs predict the convergence of Stochastic Gradient Descent visualization" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <a href="https://arxiv.org/abs/2408.01736v1" target="_blank" rel="noopener noreferrer" 
                   className="font-display text-3xl font-semibold text-[#4f4339] transition-colors duration-300 group-hover:text-[#6f6257]">
                  {t('publications.papers.sgdICL.title')}
                </a>
                <div className="flex items-center mt-2 mb-3">
                  <span className="text-sm font-semibold text-[#8a7767] bg-[#f5efe8] px-3 py-1 rounded-full">
                    {t('publications.papers.sgdICL.conference')}
                  </span>
                </div>
                <p className="text-stone-700 mb-3">
                  <span className="font-medium">{t('publications.authors')}:</span> {t('publications.papers.sgdICL.authors')}
                </p>
                <p className="text-stone-600 text-sm mb-3">
                  {t('publications.papers.sgdICL.description')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://arxiv.org/abs/2408.01736v1" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    {t('publications.paper')}
                  </a>
                  <a href="/files/publications/SGDICL_Workshop_Poster.pdf" target="_blank" rel="noopener noreferrer" 
                     className="inline-flex items-center px-3 py-1 bg-[#ede9e2] text-stone-700 text-sm rounded-lg hover:bg-[#e4ddd3] transition-colors duration-200">
                    <FileText className="w-4 h-4 mr-1" />
                    {t('publications.poster')}
                  </a>
                  <button 
                    onClick={() => setShowSGDICLCitation(!showSGDICLCitation)}
                    className="inline-flex items-center px-3 py-1 bg-[#f3ede6] text-[#6f6257] text-sm rounded-lg hover:bg-[#ece3da] transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Cite
                  </button>
                </div>
                
                {showSGDICLCitation && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-700">BibTeX Citation:</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`@article{zekri2024can,
  title={Can LLMs predict the convergence of Stochastic Gradient Descent?},
  author={Zekri, Oussama and Benechehab, Abdelhakim and Redko, Ievgen},
  journal={arXiv preprint arXiv:2408.01736},
  year={2024}
}`);
                          setCopiedSGDICL(true);
                          setTimeout(() => setCopiedSGDICL(false), 2000);
                        }}
                        className="inline-flex items-center px-2 py-1 bg-[#f3ede6] text-[#6f6257] text-xs rounded hover:bg-[#ece3da] transition-colors duration-200"
                      >
                        {copiedSGDICL ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copiedSGDICL ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <pre className="text-xs text-gray-600 bg-white p-3 rounded border overflow-x-auto">
                      {/* eslint-disable-next-line no-useless-escape */}
{`@article{zekri2024can,
  title={Can LLMs predict the convergence of Stochastic Gradient Descent?},
  author={Zekri, Oussama and Benechehab, Abdelhakim and Redko, Ievgen},
  journal={arXiv preprint arXiv:2408.01736},
  year={2024}
}`}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Publications; 
